/**
 * ServiceHostsSection — manages CIs that compose a service.
 * Shows the list of associated CIs and lets add/remove them.
 *
 * The "add" flow lets you:
 *   1. Select CI type (server, VM, printer...)
 *   2. Browse a list of available CIs of that type, or search for a specific one
 *   3. Pick one and optionally assign a role
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  PlusIcon, TrashIcon, ServerStackIcon, MagnifyingGlassIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import CiSelector from './CiSelector';
import { upperName } from '../utils/normalize';

const CI_ICONS = {
  rack_element:      '🖥️',
  virt_vm:           '💻',
  printer:           '🖨️',
  wifi_controller:   '📡',
  wifi_access_point: '📶',
  firewall:          '🛡️',
  router:            '🌐',
  workstation:       '🖥️',
  device:            '📟',
  storage_disk:      '💾',
  isp_circuit:       '🔌',
  service:           '⚙️',
  ipam_ip:           '📟',
};

// El backend llama «Rack element» / «Virt VM» a los tipos. En la ficha se
// habla de servidores y máquinas virtuales, no del nombre interno del modelo.
const CI_TYPE_LABELS = {
  rack_element: 'Servidor físico',
  virt_vm: 'Máquina virtual',
  printer: 'Impresora',
  wifi_controller: 'Controlador WiFi',
  wifi_access_point: 'Punto de acceso',
  firewall: 'Firewall',
  router: 'Router',
  workstation: 'Estación de trabajo',
  device: 'Dispositivo',
  storage_disk: 'Disco',
  isp_circuit: 'Circuito ISP',
  service: 'Servicio',
  ipam_ip: 'Dirección IP',
};

const typeLabel = (value) => CI_TYPE_LABELS[value] || value || 'Equipo';

// Map hosting_type → allowed CI types
const HOSTING_CI_MAP = {
  bare_metal: ['rack_element'],
  vm:         ['virt_vm'],
  docker:     ['rack_element', 'virt_vm'],
  cloud_vm:   ['virt_vm'],
  saas:       [],       // no CIs for external services
  paas:       [],       // no CIs for external services
  unknown:    ['rack_element', 'virt_vm', 'printer', 'wifi_controller', 'firewall', 'router', 'workstation'],
};

export default function ServiceHostsSection({ serviceId, companyId, hostingType, editable = true }) {
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [ciType, setCiType] = useState('');
  const [ciId, setCiId] = useState('');
  const [role, setRole] = useState('');
  const [saving, setSaving] = useState(false);

  const [browseMode, setBrowseMode] = useState(false);
  const [browseResults, setBrowseResults] = useState([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseSearch, setBrowseSearch] = useState('');
  const [ciTypes, setCiTypes] = useState([]);

  const load = useCallback(async () => {
    if (!serviceId || !companyId) return;
    setLoading(true);
    setError(null);
    try {
      const [res, metaRes] = await Promise.all([
        api.getServiceHosts(serviceId, companyId),
        api.getServiceAccessPointsMeta(companyId),
      ]);
      setHosts(res.hosts || []);
      if (metaRes.ci_types) setCiTypes(metaRes.ci_types);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [serviceId, companyId]);

  useEffect(() => { load(); }, [load]);

  const loadBrowseList = useCallback(async (type, q) => {
    if (!type) return;
    setBrowseLoading(true);
    try {
      const res = await api.ciSearch(companyId, { types: type, q: q || '', limit: 50 });
      setBrowseResults(res.results || []);
    } catch (_) {
      setBrowseResults([]);
    } finally {
      setBrowseLoading(false);
    }
  }, [companyId]);

  const handleTypeChange = (type) => {
    setSelectedType(type);
    setCiType(''); setCiId('');
    setBrowseMode(false); setBrowseSearch(''); setBrowseResults([]);
  };

  const handleAdd = async () => {
    if (!ciType || !ciId) { setError('Selecciona un equipo'); return; }
    setSaving(true);
    setError(null);
    try {
      await api.createServiceHost(serviceId, { ci_type: ciType, ci_id: ciId, role: role || null }, companyId);
      resetForm();
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (host) => {
    if (!window.confirm(`¿Eliminar ${host.ci_name || `#${host.ci_id}`} del servicio?`)) return;
    try {
      await api.deleteServiceHost(serviceId, host.id, companyId);
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const resetForm = () => {
    setCiType(''); setCiId(''); setRole(''); setSelectedType('');
    setBrowseMode(false); setBrowseSearch(''); setBrowseResults([]);
    setShowForm(false);
  };

  const existingKeys = new Set(hosts.map(h => `${h.ci_type}-${h.ci_id}`));

  // CI types allowed by this hosting_type
  const allowedTypes = HOSTING_CI_MAP[hostingType] || HOSTING_CI_MAP.unknown;
  const filteredCiTypes = (ciTypes || []).filter(t => allowedTypes.includes(t.value));
  const selectedTypeLabel = typeLabel(selectedType);

  const getCiTypeHint = () => {
    if (!hostingType || hostingType === 'unknown') return '';
    const hints = {
      bare_metal: 'El servicio corre en un servidor físico del inventario.',
      vm: 'El servicio corre dentro de una máquina virtual.',
      docker: 'Servidor o máquina virtual donde está instalado Docker.',
      cloud_vm: 'Máquina virtual alojada en la nube.',
    };
    return hints[hostingType] || '';
  };

  return (
    <div className="sf-hosts">
      {hosts.length > 0 && (
      <div className="sf-hosts-toolbar">
        <div className="sf-hosts-summary">
          <div className="sf-hosts-count">
            <ServerStackIcon />
            {hosts.length > 0
              ? `${hosts.length} equipo${hosts.length !== 1 ? 's' : ''}`
              : 'Sin equipos asociados'}
          </div>
          {getCiTypeHint() && hosts.length > 0 && (
            <div className="sf-hosts-hint">{getCiTypeHint()}</div>
          )}
        </div>
        {editable && !showForm && (
          <button type="button" className="dv-btn dv-btn-primary sf-hosts-add" onClick={() => setShowForm(true)}>
            <PlusIcon /> Añadir equipo
          </button>
        )}
      </div>
      )}

      {error && <div className="alert alert-danger py-1" style={{ fontSize: '0.78rem', marginBottom: 8 }}>⚠ {error}</div>}
      {loading && <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', padding: 8 }}>Cargando…</div>}

      {/* ── Add form ── */}
      {showForm && editable && (
        <div className="sf-host-form">
          <div className="sf-host-form-head">
            <div>
              <strong>Añadir equipo</strong>
              <span>Elige el tipo y búscalo en el inventario.</span>
            </div>
            <button type="button" className="sf-host-form-close" onClick={resetForm} title="Cerrar">
              <XMarkIcon />
            </button>
          </div>

          <div className="sf-host-form-grid">
            <label className="sf-host-field">
              <span>Tipo de equipo</span>
              <select className="form-select form-select-sm" value={selectedType} onChange={e => handleTypeChange(e.target.value)}>
                <option value="">— seleccionar —</option>
                {filteredCiTypes.map(t => (
                  <option key={t.value} value={t.value}>{CI_ICONS[t.value] || '📦'} {typeLabel(t.value)}</option>
                ))}
              </select>
            </label>
            {selectedType && !browseMode && (
              <>
                <div className="sf-host-field sf-host-field-search">
                  <span>Equipo del inventario</span>
                  <CiSelector ciType={ciType} ciId={ciId} companyId={companyId}
                    fixedType={selectedType}
                    onSelect={(ci) => { setCiType(ci.ci_type); setCiId(ci.ci_id); }}
                    onClear={() => { setCiType(''); setCiId(''); }} />
                </div>
                <button type="button" className="dv-btn dv-btn-secondary sf-host-browse-btn" onClick={() => {
                  setBrowseMode(true); setBrowseSearch('');
                  loadBrowseList(selectedType, '');
                }}>
                  <MagnifyingGlassIcon /> Ver todos
                </button>
              </>
            )}
          </div>

          {getCiTypeHint() && <p className="sf-host-form-hint">{getCiTypeHint()}</p>}

          {browseMode && selectedType && (
            <div className="sf-browse-overlay" onClick={() => setBrowseMode(false)}>
              <div className="sf-browse-modal" onClick={e => e.stopPropagation()}>
                <div className="sf-browse-head">
                  <div>
                    <strong>Elegir {selectedTypeLabel.toLowerCase()}</strong>
                    <span>Equipos disponibles en el inventario.</span>
                  </div>
                  <button type="button" className="sf-host-form-close" onClick={() => setBrowseMode(false)} title="Cerrar">
                    <XMarkIcon />
                  </button>
                </div>
                <div className="sf-browse-filter">
                  <input className="form-control form-control-sm" placeholder="Buscar por nombre…"
                    value={browseSearch} autoFocus
                    onChange={e => { setBrowseSearch(e.target.value); loadBrowseList(selectedType, e.target.value); }} />
                </div>
                <div className="sf-browse-list">
                  {browseLoading && <div className="sf-browse-empty">Cargando equipos…</div>}
                  {!browseLoading && browseResults.length === 0 && (
                    <div className="sf-browse-empty">
                      No hay equipos de este tipo.
                      {browseSearch && ' Prueba con otro nombre.'}
                    </div>
                  )}
                  {!browseLoading && browseResults.length > 0 && browseResults.map(ci => {
                    const key = `${ci.ci_type}-${ci.ci_id}`;
                    const alreadyAdded = existingKeys.has(key);
                    const selected = ciType === ci.ci_type && ciId === ci.ci_id;
                    return (
                      <button
                        key={key}
                        type="button"
                        className={`sf-browse-item${alreadyAdded ? ' is-added' : ''}${selected ? ' is-selected' : ''}`}
                        onClick={() => {
                          if (alreadyAdded) return;
                          setCiType(ci.ci_type); setCiId(ci.ci_id);
                          setBrowseMode(false);
                        }}
                        disabled={alreadyAdded}
                      >
                        <span className="sf-browse-item-icon">{CI_ICONS[ci.ci_type] || '📦'}</span>
                        <span className="sf-browse-item-body">
                          <span className="sf-browse-item-name">{upperName(ci.name)}</span>
                          {ci.description && <span className="sf-browse-item-desc">{ci.description}</span>}
                        </span>
                        {alreadyAdded && <span className="sf-browse-item-added">Ya añadido</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <div className="sf-host-form-actions">
            <label className="sf-host-field sf-host-field-role">
              <span>Rol (opcional)</span>
              <input className="form-control form-control-sm" value={role}
                onChange={e => setRole(e.target.value)} placeholder="principal, worker…" />
            </label>
            <div className="sf-host-form-buttons">
              <button type="button" className="dv-btn dv-btn-primary" onClick={handleAdd}
                disabled={saving || !ciType || !ciId}>
                {saving ? 'Añadiendo…' : 'Añadir equipo'}
              </button>
              <button type="button" className="dv-btn dv-btn-secondary" onClick={resetForm}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Equipos asociados ── */}
      {!loading && hosts.length > 0 && (
        <div className="sf-hosts-list">
          {hosts.map(host => (
            <div key={host.id} className="sf-host-item">
              <div className="sf-host-item-icon"><ServerStackIcon /></div>
              <div className="sf-host-item-body">
                <div className="sf-host-item-name">
                  {host.ci_name ? upperName(host.ci_name) : `${typeLabel(host.ci_type)} #${host.ci_id}`}
                </div>
                <div className="sf-host-item-meta">{typeLabel(host.ci_type)}</div>
              </div>
              {host.role && <span className="sf-host-role">{host.role}</span>}
              {editable && (
                <button type="button" className="sf-host-remove" onClick={() => handleDelete(host)} title="Quitar este equipo">
                  <TrashIcon />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && hosts.length === 0 && !showForm && (
        <div className="sf-hosts-empty">
          <div className="sf-hosts-empty-icon"><ServerStackIcon /></div>
          <strong>{editable ? 'Este servicio aún no tiene equipos' : 'Sin equipos asociados'}</strong>
          <span>
            {editable
              ? 'Añade el servidor, la máquina virtual o el dispositivo donde se ejecuta.'
              : 'No hay equipos vinculados a este servicio.'}
          </span>
          {editable && (
            <button type="button" className="dv-btn dv-btn-secondary" onClick={() => setShowForm(true)}>
              <PlusIcon /> Añadir el primero
            </button>
          )}
        </div>
      )}
    </div>
  );
}
